import { DEFAULT_MA_TELOP_FONT_KEY, resolveMaTelopFont } from './maTelopFonts';

/**
 * MA テロップ（タイムチャート焼き込み）の表示設定。
 *
 * 書き出しモーダルの「表示設定」で編集し、buildMaTelopAss が ASS に変換する。
 * 寸法（fontSize / margin / outlineWidth）はすべて **1080p 基準の px** で、
 * 実際の動画サイズに応じて buildMaTelopAss 側でスケールされる。
 *
 * 将来プロジェクト設定（BE の JSONField）に保存する前提で、BE から来た
 * 未知の形の値も normalizeMaTelopConfig で既定値に補正できるようにしてある。
 */

export type MaTelopPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type MaTelopTimestampFormat = 'timecode' | 'frames';
export type MaTelopDurationMode = 'clip' | 'fixed';

export interface MaTelopTrackColor {
  /** トラック名（RPP の Render Track List / CSV の track 列。大文字小文字は区別しない） */
  track: string;
  /** 背景色 `#RRGGBB` */
  color: string;
}

export interface MaTelopConfig {
  /** maTelopFonts.ts のキー */
  fontKey: string;
  position: MaTelopPosition;
  /** 文字サイズ（1080p 基準 px） */
  fontSize: number;
  /** 基準端からの水平マージン（1080p 基準 px） */
  marginX: number;
  /** 基準端からの垂直マージン（1080p 基準 px） */
  marginY: number;
  /** 文字色 `#RRGGBB` */
  fontColor: string;
  /** 文字の黒縁取り */
  textOutline: boolean;
  /** 縁取り幅（1080p 基準 px） */
  outlineWidth: number;
  /** 先頭にタイムスタンプ（クリップ開始位置）を表示 */
  showTimestamp: boolean;
  /** タイムスタンプ形式: M:SS.FF / 絶対フレーム（300F） */
  timestampFormat: MaTelopTimestampFormat;
  /** メディア名（拡張子なし）を表示 */
  showMediaName: boolean;
  /**
   * メディア名を `_` 区切りで短縮表示する（例: `BGM_1234_あたり` → 範囲 2 → `BGM_1234`）。
   * 表示名 CSV で置き換わった名前には適用しない（明示指定はそのまま出す）
   */
  nameDelimiterEnabled: boolean;
  /** nameDelimiterEnabled 時に先頭から残す区切り数（1〜20） */
  nameSegmentCount: number;
  /** トラック名を表示 */
  showTrackName: boolean;
  /** クリップゲイン（0dB 以外）を表示 */
  showGain: boolean;
  /** 表示時間: クリップの実効長 / 固定秒 */
  durationMode: MaTelopDurationMode;
  /** durationMode = fixed のときの表示秒数 */
  fixedDurationSeconds: number;
  /**
   * テロップ背景（トラック色のボックス）を描く。
   * false = 無色（背景なし。文字 + 縁取りのみ）。既定は無色
   */
  showBackground: boolean;
  /** トラック名 → 背景色 */
  trackColors: MaTelopTrackColor[];
  /** trackColors に無いトラックの背景色 `#RRGGBB` */
  defaultTrackColor: string;
}

export const MA_TELOP_POSITIONS: ReadonlyArray<{ value: MaTelopPosition; label: string }> = [
  { value: 'top-left', label: '左上' },
  { value: 'top-right', label: '右上' },
  { value: 'bottom-left', label: '左下' },
  { value: 'bottom-right', label: '右下' },
];

/**
 * 既定のトラック色。MA 自動化スクリプト（ma_automation.py）の TRACK_COLOR_MAP を
 * ASS BGR から `#RRGGBB` に変換して移植したもの
 */
export const DEFAULT_MA_TELOP_TRACK_COLORS: readonly MaTelopTrackColor[] = [
  { track: 'BGM', color: '#00FFFF' },
  { track: 'BGM1', color: '#00FFFF' },
  { track: 'BGM2', color: '#0080FF' },
  { track: 'SFX', color: '#FF8000' },
  { track: 'SFX1', color: '#FFFF00' },
  { track: 'SFX2', color: '#FF8000' },
  { track: 'SFX3', color: '#FF4000' },
  { track: 'FOLEY', color: '#FFFF00' },
  { track: 'UI', color: '#FF4000' },
  { track: 'GIMMICK', color: '#FF4000' },
  { track: 'VC', color: '#FFFF80' },
  { track: 'VC1', color: '#FFFF80' },
  { track: 'VC2', color: '#FF00FF' },
  { track: 'VC3', color: '#FF0080' },
  { track: 'PREVIEW Only', color: '#008080' },
  { track: 'NEW', color: '#00FFFF' },
  { track: 'UPDATE', color: '#FF8000' },
  { track: 'KEEP', color: '#FFFF00' },
];

export const DEFAULT_MA_TELOP_CONFIG: MaTelopConfig = {
  fontKey: DEFAULT_MA_TELOP_FONT_KEY,
  position: 'top-left',
  fontSize: 48,
  marginX: 32,
  marginY: 32,
  fontColor: '#FFFFFF',
  textOutline: true,
  outlineWidth: 2,
  showTimestamp: true,
  timestampFormat: 'timecode',
  showMediaName: true,
  nameDelimiterEnabled: false,
  nameSegmentCount: 2,
  showTrackName: false,
  showGain: false,
  durationMode: 'clip',
  fixedDurationSeconds: 2,
  showBackground: false,
  trackColors: [...DEFAULT_MA_TELOP_TRACK_COLORS],
  defaultTrackColor: '#65555A',
};

/** 既定設定の編集可能なコピー（trackColors 配列も複製する） */
export function createDefaultMaTelopConfig(): MaTelopConfig {
  return { ...DEFAULT_MA_TELOP_CONFIG, trackColors: [...DEFAULT_MA_TELOP_TRACK_COLORS] };
}

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** `#RRGGBB` として妥当か */
export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR_RE.test(value);
}

function pickNumber(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

function pickBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function pickEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function pickTrackColors(value: unknown): MaTelopTrackColor[] {
  if (!Array.isArray(value)) return [...DEFAULT_MA_TELOP_TRACK_COLORS];
  const result: MaTelopTrackColor[] = [];
  value.forEach(entry => {
    if (!entry || typeof entry !== 'object') return;
    const track = (entry as { track?: unknown }).track;
    const color = (entry as { color?: unknown }).color;
    if (typeof track !== 'string' || !isHexColor(color)) return;
    result.push({ track, color: color.toUpperCase() });
  });
  return result;
}

/**
 * 任意の JSON 値を MaTelopConfig に正規化する（欠損は既定値、不正値は既定値へ）。
 * BE 保存値 / 旧形式 / ユーザー入力途中の値のいずれにも使える
 */
export function normalizeMaTelopConfig(raw: unknown): MaTelopConfig {
  const d = DEFAULT_MA_TELOP_CONFIG;
  if (!raw || typeof raw !== 'object') return createDefaultMaTelopConfig();
  const r = raw as Record<string, unknown>;
  return {
    fontKey: resolveMaTelopFont(typeof r.fontKey === 'string' ? r.fontKey : null).key,
    position: pickEnum(
      r.position,
      MA_TELOP_POSITIONS.map(p => p.value),
      d.position,
    ),
    fontSize: Math.round(pickNumber(r.fontSize, d.fontSize, 12, 200)),
    marginX: Math.round(pickNumber(r.marginX, d.marginX, 0, 800)),
    marginY: Math.round(pickNumber(r.marginY, d.marginY, 0, 800)),
    fontColor: isHexColor(r.fontColor) ? r.fontColor.toUpperCase() : d.fontColor,
    textOutline: pickBoolean(r.textOutline, d.textOutline),
    outlineWidth: Math.round(pickNumber(r.outlineWidth, d.outlineWidth, 0, 10)),
    showTimestamp: pickBoolean(r.showTimestamp, d.showTimestamp),
    timestampFormat: pickEnum(r.timestampFormat, ['timecode', 'frames'], d.timestampFormat),
    showMediaName: pickBoolean(r.showMediaName, d.showMediaName),
    nameDelimiterEnabled: pickBoolean(r.nameDelimiterEnabled, d.nameDelimiterEnabled),
    nameSegmentCount: Math.round(pickNumber(r.nameSegmentCount, d.nameSegmentCount, 1, 20)),
    showTrackName: pickBoolean(r.showTrackName, d.showTrackName),
    showGain: pickBoolean(r.showGain, d.showGain),
    durationMode: pickEnum(r.durationMode, ['clip', 'fixed'], d.durationMode),
    fixedDurationSeconds: pickNumber(r.fixedDurationSeconds, d.fixedDurationSeconds, 0.1, 600),
    showBackground: pickBoolean(r.showBackground, d.showBackground),
    trackColors: pickTrackColors(r.trackColors),
    defaultTrackColor: isHexColor(r.defaultTrackColor)
      ? r.defaultTrackColor.toUpperCase()
      : d.defaultTrackColor,
  };
}

/**
 * `_` 区切りの短縮表示ルールを適用する。
 * 有効時は先頭から nameSegmentCount 個の区切りだけを残す
 * （例: `BGM_1234_あたり` + 範囲 2 → `BGM_1234`）。無効時はそのまま返す。
 * 表示名 CSV で置き換わった名前には適用しないこと（呼び出し側で分岐）
 */
export function applyDelimiterRule(name: string, config: MaTelopConfig): string {
  if (!config.nameDelimiterEnabled) return name;
  return name
    .split('_')
    .slice(0, Math.max(1, config.nameSegmentCount))
    .join('_');
}

/**
 * トラック名に対応する背景色を返す（大文字小文字を区別せず完全一致。該当なしは既定色）。
 * trackName が null のときはトラック番号の文字列で照合する
 */
export function resolveMaTelopTrackColor(
  config: MaTelopConfig,
  trackName: string | null,
  trackNumber: number,
): string {
  const key = (trackName ?? (trackNumber > 0 ? String(trackNumber) : '')).trim().toLowerCase();
  if (key) {
    const hit = config.trackColors.find(entry => entry.track.trim().toLowerCase() === key);
    if (hit) return hit.color;
  }
  return config.defaultTrackColor;
}
